import React from "react";
import { observable } from "mobx";
import { decorate } from "mobx";
import { observer } from "mobx-react";
import Keyframe from "./keyframe";
import { roundTo as round } from "../../utils";

function compareKeyframeValues(v1, v2) {
   // Video time values - compare with rounding for display
   return round(v1, 2) === round(v2, 2);
}

// RawVideoGroupMarker component - renders a draggable rect for a group of keyframes
class RawVideoGroupMarkerComponent extends React.Component {
   constructor(props) {
      super(props);
      this.isDragging = false;
      this.dragStartX = 0;
   }

   mouseDown = (e) => {
      const { group, timelineState, layoutState } = this.props;
      const { selections } = layoutState;
      const { keyframes } = group;

      const isLocked = timelineState.videoLocked;
      if (isLocked) return;

      // Check if any keyframe in group is already selected
      const anySelected = keyframes.some((kf) =>
         selections.keyframes.includes(kf),
      );

      if (!anySelected && !e.shiftKey) {
         selections.clearAllSelections();
      }

      // Select all keyframes in the group
      keyframes.forEach((kf, index) => {
         // Use shift=true for all but the first to add to selection
         selections.setKeyframeSelection(kf, index > 0 || e.shiftKey);
      });

      const NODE = e.target.ownerDocument;
      NODE.addEventListener("mousemove", this.mouseMove, false);
      NODE.addEventListener("mouseup", this.mouseUp, false);
      this.dragStartX = e.clientX;
      this.isDragging = true;

      // Start drag transaction with the first keyframe's ID
      // (the system will move all selected keyframes)
      timelineState.dragTransaction.start(keyframes[0].objectId);
   };

   mouseMove = (e) => {
      const { frameWidth, timelineState } = this.props;
      const diff = e.clientX - this.dragStartX;
      const frameDiff = Math.floor(diff / frameWidth);
      timelineState.dragTransaction.move(frameDiff);
   };

   mouseUp = (e) => {
      const { timelineState } = this.props;
      const NODE = e.target.ownerDocument;
      NODE.removeEventListener("mousemove", this.mouseMove, false);
      NODE.removeEventListener("mouseup", this.mouseUp, false);
      this.isDragging = false;

      // dragTransaction.end() will automatically call selections.shiftTimelineSelected()
      // which updates all selected keyframes
      timelineState.dragTransaction.end();
   };

   render() {
      const {
         group,
         timelineState,
         layoutState,
         frames,
         frameWidth,
         offset,
         width,
      } = this.props;
      const { keyframes } = group;
      const { keyframes: selections } = layoutState.selections;

      if (keyframes.length === 0) return null;

      // Calculate start and end from keyframes
      const sortedKeyframes = [...keyframes].sort((a, b) => a.frame - b.frame);
      const startFrame = sortedKeyframes[0].frame;
      const endFrame = sortedKeyframes[sortedKeyframes.length - 1].frame;
      const groupDuration = Math.max(endFrame - startFrame + 1, 1); // at least 1 frame wide

      const isDisplayed = startFrame <= offset + frames && endFrame >= offset;
      const anySelected = keyframes.some((kf) => selections.includes(kf));
      const isLocked = timelineState.videoLocked;

      const isDragging = this.isDragging;
      let rectClass = "";
      if (anySelected) rectClass += " selected";
      if (isDragging) rectClass += " dragging";

      if (isDisplayed) {
         const dragOffset = anySelected
            ? timelineState.dragTransaction.offset
            : 0;
         const x = (startFrame + dragOffset - offset) * frameWidth;
         const rectWidth = groupDuration * frameWidth;

         const displayName = group.label || "Unlabeled Group";
         const clipPathId = `group-${group.label}-${startFrame}`;

         return (
            <g>
               <clipPath id={clipPathId}>
                  <rect x={x} y="0" width={rectWidth} height="15" />
               </clipPath>
               <rect
                  className={rectClass}
                  x={x}
                  y="0"
                  width={rectWidth}
                  height="19"
                  fill="#FFFFFF"
                  rx="2"
                  ry="2"
                  onMouseDown={this.mouseDown}
                  style={{ cursor: isLocked ? "default" : "pointer" }}
               />
               <g clipPath={`url(#${clipPathId})`}>
                  <text
                     x={x + 5}
                     y="13"
                     fontFamily="Roboto"
                     fontSize="10"
                     fill="#333333"
                     style={{ pointerEvents: "none" }}
                  >
                     {displayName}
                  </text>
               </g>
            </g>
         );
      } else {
         return null;
      }
   }
}

decorate(RawVideoGroupMarkerComponent, {
   isDragging: observable,
});

const RawVideoGroupMarker = observer(RawVideoGroupMarkerComponent);

// Refactored MasterRawVideoKeyframeTimeline - now renders draggable rect groups
const MasterRawVideoKeyframeTimeline = observer(({ video, width, ...rest }) => {
   if (!video || video.length === 0) return null
   const { layoutState } = rest
   const { selections } = layoutState
   let isTimelineSelected = false

   // Group consecutive keyframes by shared label (duplicate logic from RawVideoKeyframeTimelines)
   const groups = []
   let currentGroup = null
   let className = 'raw-video-keyframe-timeline'
   let isSelected = false

   for (const kf of video) {
     isSelected = isSelected || selections.keyframes.includes(kf)
      if (!currentGroup || currentGroup.label !== kf.label) {
         currentGroup = { label: kf.label, keyframes: [] }
         groups.push(currentGroup)
      }
      currentGroup.keyframes.push(kf)
   }

   const groupMarkers = groups.map((group, index) => (
      <RawVideoGroupMarker
         key={`${group.label}-${index}`}
         group={group}
         width={width}
         {...rest}
      />
   ))
   if (isSelected) className += ' selected'

   return (
      <div className={className}>
         <div className="grid">
            <svg
               width={width}
               height="19"
               style={{ shapeRendering: "optimizeSpeed" }}
            >
               <g>{groupMarkers}</g>
            </svg>
         </div>
      </div>
   );
});

const RawVideoKeyframeTimelines = observer(({ video, ...rest }) => {
   if (!video || video.length === 0) return null;

   // Group consecutive keyframes by shared label
   const groups = [];
   let currentGroup = null;

   for (const kf of video) {
      if (!currentGroup || currentGroup.label !== kf.label) {
         currentGroup = { label: kf.label, keyframes: [] };
         groups.push(currentGroup);
      }
      currentGroup.keyframes.push(kf);
   }

   const timelines = groups.map((group) => (
      <RawVideoKeyframeTimeline
         key={group.label}
         videoKeyframes={group.keyframes}
         {...rest}
      />
   ));

   return <div className="raw-video-keyframe-timelines">{timelines}</div>;
});

const RawVideoKeyframeTimeline = observer(
   class RawVideoKeyframeTimeline extends React.Component {
      onMouseDown = (e) => {
         const { layoutState } = this.props;
         if (!e.shiftKey) layoutState.selections.clearAllSelections();
      };

      selectKeyframe = (keyframe, shiftKey) => {
         const { timelineState, layoutState } = this.props;
         const { selections } = layoutState;
         const isSelected = selections.keyframes.includes(keyframe);
         if (!isSelected && !shiftKey) {
            selections.clearAllSelections();
         }
         selections.setKeyframeSelection(keyframe, shiftKey);
      };

      render() {
         const {
            timelineState,
            layoutState,
            videoKeyframes,
            frames,
            frameWidth,
            offset,
            width,
            stage,
         } = this.props;
         const { keyframes: selections } = layoutState.selections;

         const keyframes = videoKeyframes || [];
         const sortedKeyframes = [...keyframes].sort(
            (a, b) => a.frame - b.frame,
         );
         const isLocked = timelineState.videoLocked || false;

         // Rendered Elements
         let kfs = [];
         let selectedKfs = [];
         let lines = [];

         // Render keyframes
         for (let i = 0; i < keyframes.length; i++) {
            let k = keyframes[i];
            if (k.frame >= offset && k.frame <= offset + frames) {
               const isSelected = selections.includes(k);
               const isDragging =
                  k.objectId === timelineState.dragTransaction.object;
               const dragOffset = isSelected
                  ? timelineState.dragTransaction.offset
                  : 0;
               const xPos = (k.frame + dragOffset - offset) * frameWidth;

               const keyframeProps = {
                  key: k.objectId,
                  x: xPos,
                  keyframe: k,
                  property: "video",
                  selectKeyframe: this.selectKeyframe,
                  timelineState,
                  frameWidth,
                  isLocked,
                  isSelected,
                  isDragging,
                  stage,
               };

               const keyframeComponent = <Keyframe {...keyframeProps} />;
               if (isSelected) {
                  selectedKfs.push(keyframeComponent);
               } else {
                  kfs.push(keyframeComponent);
               }
            }
         }

         // Draw tween lines between keyframes
         const sortedEnd = sortedKeyframes.length;
         for (let i = 1; i < sortedEnd; i++) {
            let k = sortedKeyframes[i];
            let prevK = sortedKeyframes[i - 1];
            let isStep = k.fn === "step";
            let isTweening = !compareKeyframeValues(k.value, prevK.value);
            let isSelected = selections.includes(k);
            let dragOffset = isSelected
               ? timelineState.dragTransaction.offset
               : 0;
            let xStart =
               (prevK.frame + dragOffset) * frameWidth - frameWidth / 2;
            let xEnd = (k.frame + dragOffset) * frameWidth - frameWidth / 2;

            if (isTweening && !isStep) {
               lines.push(
                  <line
                     x1={xStart}
                     y1={10}
                     x2={xEnd}
                     y2={10}
                     stroke="#FFFFFF"
                     strokeWidth="1"
                     key={`${k.objectId}-tween`}
                  />,
               );
            }
         }

         return (
            <div
               className="raw-video-keyframe-timeline"
               onMouseDown={this.onMouseDown}
            >
               <div className="grid">
                  <svg
                     width={width}
                     height="20"
                     className="row-rect above"
                     style={{ shapeRendering: "optimizeSpeed" }}
                  >
                     {lines}
                     {kfs}
                     {selectedKfs}
                  </svg>
               </div>
            </div>
         );
      }
   },
);

export { MasterRawVideoKeyframeTimeline, RawVideoKeyframeTimelines };
